import i18next from 'i18next';

type DimensionValue = number | string | null | undefined;

export const DIMENSION_DISPLAY_PROFILES = ['GENERIC', 'BOARD', 'WINDOW', 'PROFILE', 'WIDTH_HEIGHT'] as const;
export type DimensionDisplayProfile = typeof DIMENSION_DISPLAY_PROFILES[number];
type DimensionLabelLanguage = 'pl' | 'en';

type ProductDimensions = {
  width?: DimensionValue;
  height?: DimensionValue;
  length?: DimensionValue;
  thickness?: DimensionValue;
  dimensionDisplayProfile?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
};

export type DimensionField = keyof Pick<ProductDimensions, 'width' | 'height' | 'length' | 'thickness'>;

export type ProductDimensionPart = {
  field: DimensionField;
  label: string;
  value: string;
};

// Mirror contract: docs/modules/dimension-display.md and backend/src/utils/dimensionDisplayProfile.js.
const DIMENSION_FIELDS_BY_PROFILE: Record<DimensionDisplayProfile, DimensionField[]> = {
  GENERIC: ['thickness', 'width', 'length', 'height'],
  BOARD: ['thickness', 'width', 'length'],
  WINDOW: ['width', 'height', 'length'],
  PROFILE: ['width', 'height', 'length'],
  WIDTH_HEIGHT: ['width', 'height'],
};

const DIMENSION_LABELS: Record<DimensionLabelLanguage, Record<DimensionField, string>> = {
  pl: {
    width: 'szer.',
    height: 'wys.',
    length: 'dł.',
    thickness: 'gr.',
  },
  en: {
    width: 'W',
    height: 'H',
    length: 'L',
    thickness: 'T',
  },
};

const WINDOW_DEPTH_LABELS: Record<DimensionLabelLanguage, string> = {
  pl: 'gł.',
  en: 'D',
};

type FormatProductDimensionOptions = {
  language?: string;
};

const normalizeDimensionValue = (value: DimensionValue): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue =
    typeof value === 'number' ? value : Number.parseFloat(String(value).replace(',', '.'));

  if (Number.isFinite(numericValue)) {
    if (numericValue === 0) {
      return null;
    }

    return Number.isInteger(numericValue) ? String(numericValue) : String(numericValue);
  }

  const trimmedValue = String(value).trim();
  if (!trimmedValue || trimmedValue === '0' || trimmedValue === '0.00') {
    return null;
  }

  return trimmedValue;
};

const normalizeDimensionLanguage = (language?: string): DimensionLabelLanguage =>
  language?.toLowerCase().startsWith('en') ? 'en' : 'pl';

export const normalizeDimensionDisplayProfile = (
  value?: string | null
): DimensionDisplayProfile | null => {
  if (!value) return null;
  const normalized = value.toUpperCase() as DimensionDisplayProfile;
  return DIMENSION_DISPLAY_PROFILES.includes(normalized) ? normalized : null;
};

export const profileUsesDimension = (
  profile: string | null | undefined,
  field: DimensionField,
): boolean => {
  const normalizedProfile = normalizeDimensionDisplayProfile(profile);
  return normalizedProfile
    ? DIMENSION_FIELDS_BY_PROFILE[normalizedProfile].includes(field)
    : false;
};

export const getDimensionDisplayProfile = (
  product?: ProductDimensions | null,
  fallback?: ProductDimensions | null
): DimensionDisplayProfile | null =>
  normalizeDimensionDisplayProfile(
    product?.dimensionDisplayProfile
    ?? product?.productCategoryDimensionDisplayProfile
    ?? fallback?.dimensionDisplayProfile
    ?? fallback?.productCategoryDimensionDisplayProfile
  );

const getProfileDimensionParts = (
  product: ProductDimensions,
  profile: DimensionDisplayProfile,
  language: DimensionLabelLanguage
): ProductDimensionPart[] =>
  DIMENSION_FIELDS_BY_PROFILE[profile]
    .map((field) => {
      if (profile === 'BOARD' && field === 'thickness') {
        return {
          field,
          value: normalizeDimensionValue(product.thickness ?? product.height),
        };
      }

      return {
        field,
        value: normalizeDimensionValue(product[field]),
      };
    })
    .filter((part): part is { field: DimensionField; value: string } => Boolean(part.value))
    .map((part) => ({
      ...part,
      label:
        profile === 'WINDOW' && part.field === 'length'
          ? WINDOW_DEPTH_LABELS[language]
          : DIMENSION_LABELS[language][part.field],
    }));

const formatProfileDimensions = (
  product: ProductDimensions,
  profile: DimensionDisplayProfile,
  language: DimensionLabelLanguage
): string | null => {
  const dimensions = getProfileDimensionParts(product, profile, language)
    .map((part) => {
      const label =
        profile === 'WINDOW' && part.field === 'length'
          ? WINDOW_DEPTH_LABELS[language]
          : DIMENSION_LABELS[language][part.field];

      return `${label} ${part.value}`;
    });

  return dimensions.length > 0 ? `${dimensions.join(' x ')} mm` : null;
};

export const getCatalogProductDimensionParts = (
  product?: ProductDimensions | null,
  options: FormatProductDimensionOptions = {}
): ProductDimensionPart[] => {
  if (!product) return [];

  const profile = getDimensionDisplayProfile(product) ?? 'GENERIC';
  const language = normalizeDimensionLanguage(options.language ?? i18next.language);
  return getProfileDimensionParts(product, profile, language);
};

export const formatProductDimensions = (
  product?: ProductDimensions | null,
  options: FormatProductDimensionOptions = {}
): string | null => {
  if (!product) {
    return null;
  }

  const language = normalizeDimensionLanguage(options.language ?? i18next.language);
  const profile = getDimensionDisplayProfile(product) ?? 'GENERIC';

  return formatProfileDimensions(product, profile, language);
};

export const formatCatalogProductDimensions = (
  product?: ProductDimensions | null,
  fallback?: ProductDimensions | null,
  options: FormatProductDimensionOptions = {}
): string | null => {
  if (!product && !fallback) {
    return null;
  }

  const dimensionsSource: ProductDimensions = {
    width: product?.width ?? fallback?.width,
    height: product?.height ?? fallback?.height,
    length: product?.length ?? fallback?.length,
    thickness: product?.thickness ?? fallback?.thickness,
    dimensionDisplayProfile: product?.dimensionDisplayProfile ?? fallback?.dimensionDisplayProfile,
    productCategoryDimensionDisplayProfile:
      product?.productCategoryDimensionDisplayProfile
      ?? fallback?.productCategoryDimensionDisplayProfile,
  };

  const profile = getDimensionDisplayProfile(product, fallback) ?? 'GENERIC';
  const language = normalizeDimensionLanguage(options.language ?? i18next.language);

  return formatProfileDimensions(dimensionsSource, profile, language);
};
